import { supabase } from '@/integrations/supabase/client';
import { UploadError } from './forumUploads';

const ALLOWED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_BYTES = 8 * 1024 * 1024; // 8 MB for blog images

function validate(file: File) {
  if (!ALLOWED.includes(file.type)) {
    throw new UploadError('Only PNG, JPEG, WEBP, or GIF images are supported.', 'type');
  }
  if (file.size > MAX_BYTES) {
    throw new UploadError('Image is too large. Maximum size is 8 MB.', 'size');
  }
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

/** Upload a blog image to the `blog-images` bucket with progress tracking. */
export async function uploadBlogImageWithProgress(
  file: File,
  onProgress?: (pct: number) => void,
): Promise<string> {
  validate(file);
  const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
  const path = `posts/${crypto.randomUUID()}.${ext}`;
  const url = `${SUPABASE_URL}/storage/v1/object/blog-images/${path}`;

  // Forward the auth token so RLS (`can_manage_cms`) passes.
  const { data: { session } } = await supabase.auth.getSession();
  const bearer = session?.access_token || SUPABASE_KEY;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);
    xhr.setRequestHeader('Authorization', `Bearer ${bearer}`);
    xhr.setRequestHeader('apikey', SUPABASE_KEY);
    xhr.setRequestHeader('x-upsert', 'false');
    xhr.setRequestHeader('Content-Type', file.type);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onerror = () => reject(new UploadError('Network error — please try again.', 'network'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const { data } = supabase.storage.from('blog-images').getPublicUrl(path);
        onProgress?.(100);
        resolve(data.publicUrl);
      } else {
        let msg = `Upload failed (${xhr.status}).`;
        try {
          const body = JSON.parse(xhr.responseText);
          if (body?.message) msg = body.message;
        } catch { /* ignore */ }
        reject(new UploadError(msg, 'server'));
      }
    };

    xhr.send(file);
  });
}
